import { takeLatest, select, call, takeEvery } from 'redux-saga/effects';
import { FeatureFlagResponse } from '@commutifi/models/FeatureFlag';
import { GetResponse } from '@commutifi-fe/interfaces';
import { fetchFeatureFlags } from 'api/modules/enterprises';
import { fetchEnterprise } from 'api/modules/user/api';
import { callApiSaga, IFetchAction } from 'store/callApiSaga';
import { IOrganization } from 'shared/interfaces';
import { arrayOfFeatureFlags, normalize, NormalizerResultForFeatureFlags, NormalizerResultForOrganizations, organizationOrEnterprise } from 'store/schemas';
import { logger } from '@commutifi-fe/logger';
import { FETCH_FEATURE_FLAGS_FAILURE, FETCH_FEATURE_FLAGS_REQUEST, FETCH_FEATURE_FLAGS_SUCCESS, FETCH_FEATURE_FLAGS_TRIGGER, FETCH_ORGANIZATION_FAILURE, FETCH_ORGANIZATION_REQUEST, FETCH_ORGANIZATION_SUCCESS, FETCH_ORGANIZATION_TRIGGER, OPTIMISTIC_UPDATE_ORGANIZATION, SET_CURRENT_ORGANIZATION_ID } from './types';
import { getById, getFeatureFlagsByEnterpriseId } from './selectors';
export const triggerFetchFeatureFlags = (enterpriseId: string | null, queryParams?: Record<string, any>, promise?: {
  resolve: typeof Promise.resolve;
  reject: typeof Promise.reject;
}): IFetchAction<NormalizerResultForFeatureFlags, Record<string, any>> => ({
  type: FETCH_FEATURE_FLAGS_TRIGGER,
  types: [FETCH_FEATURE_FLAGS_REQUEST, FETCH_FEATURE_FLAGS_SUCCESS, FETCH_FEATURE_FLAGS_FAILURE],
  callAPI: () => enterpriseId ? fetchFeatureFlags(enterpriseId, queryParams) : logger.captureWarning('Cannot fetch feature flags without an enterpriseId'),
  payload: {
    ...queryParams,
    enterpriseId
  },
  // We store all feature flags by the original requested enterprise id since
  // those might fallback to a default value without any enterprise id
  normalize: (data: GetResponse<FeatureFlagResponse>): NormalizerResultForFeatureFlags => normalize(data.records.map(feature => ({
    ...feature,
    enterpriseId
  })), arrayOfFeatureFlags),
  promise
});
export const triggerFetchOrganization = (organizationId: string): IFetchAction<NormalizerResultForOrganizations, Record<string, any>> => ({
  type: FETCH_ORGANIZATION_TRIGGER,
  types: [FETCH_ORGANIZATION_REQUEST, FETCH_ORGANIZATION_SUCCESS, FETCH_ORGANIZATION_FAILURE],
  callAPI: () => fetchEnterprise(organizationId),
  payload: {
    organizationId
  },
  normalize: (data: any): NormalizerResultForOrganizations => normalize(data, organizationOrEnterprise)
});
export const optimisticUpdateOrganization = (organizationId: string, data: Partial<IOrganization>) => ({
  type: OPTIMISTIC_UPDATE_ORGANIZATION,
  payload: {
    organizationId,
    data
  }
});
export const setCurrentOrganizationId = (organizationId: string | null) => ({
  type: SET_CURRENT_ORGANIZATION_ID,
  payload: {
    organizationId
  }
});
function* fetchFeatureFlagsSaga(action: ReturnType<typeof triggerFetchFeatureFlags>) {
  // Skip fetching in test environment if feature flags are already loaded. This will save you frustrations when mocking the current enterprise
  // or when you are overriding the initial state with partial enterprise feature flags
  if (process.env.NODE_ENV === 'test') {
    const featureFlagsByEnterpriseId: Record<string, FeatureFlagResponse> = yield select(getFeatureFlagsByEnterpriseId);
    const enterpriseId = action.payload?.enterpriseId;
    if (enterpriseId && featureFlagsByEnterpriseId?.[enterpriseId]) {
      // Feature flags already exist for this enterprise, skip fetching
      return;
    }
  }

  // Proceed with the normal fetch
  yield call(callApiSaga, action);
}
function* fetchCurrentOrganizationSaga(action: ReturnType<typeof triggerFetchOrganization>) {
  // Skip fetching in test environment if feature flags are already loaded. This will save you frustrations when mocking the current organization
  // or when you are overriding the initial state with partial organization
  if (process.env.NODE_ENV === 'test') {
    const orgById = yield select(getById);
    const organizationId = action.payload?.organizationId;
    if (organizationId && orgById?.[organizationId]) {
      return;
    }
  }
  yield call(callApiSaga, action);
}
export const enterprisesSagas = [takeEvery(FETCH_FEATURE_FLAGS_TRIGGER, fetchFeatureFlagsSaga), takeLatest(FETCH_ORGANIZATION_TRIGGER, fetchCurrentOrganizationSaga)];